import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { GetNewExerciseDraft } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { ExerciseCategoryInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseCategoryInput"
import { ExerciseChronoForm } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseChronoForm"
import { ExerciseChronoInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseChronoInput"
import { ExerciseDescriptionInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseDescriptionInput"
import { ExerciseDurationInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseDurationInput"
import { ExerciseFormHeader } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseFormHeader"
import { ExerciseMediaRequirementInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseMediaRequirementInput"
import { ExerciseRpeInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseRpeInput"
import { ExerciseScoreForm } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseScoreForm"
import { ExerciseScoreInput } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseScoreInput"
import {
  getExerciseBlockValidationSchema,
  type ExerciseFormikType,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { type HURichTextInputRef } from "@/modules/ui/components/richText/huRichTextInput"
import { word } from "@modules/core/utils/i18n"
import { useFormik } from "formik"
import { XIcon } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

export type ExerciseFormViewEnum = "exerciseContent" | "scoreContent" | "chronoContent"

export function ExerciseForm({
  editedExercise,
  updatedExerciseIndex,
  onValidate,
  className,
  ...props
}: {
  editedExercise?: ExerciseFormikType
  updatedExerciseIndex: number
  onValidate: (exercise: ExerciseFormikType) => Promise<void>
} & React.ComponentProps<"div">) {
  const [currentView, setCurrentView] = useState<ExerciseFormViewEnum>("exerciseContent")
  const onCloseSidebarFnRef = useRef<() => void>(() => {})
  const onCloseExerciseFormFnRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true))
  const { updateSidebarStackOnTop, hideSidebarStackOnTop, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()

  const exerciseFormik = useFormik<ExerciseFormikType>({
    initialValues: editedExercise ?? GetNewExerciseDraft(),
    validationSchema: getExerciseBlockValidationSchema(),
    onSubmit: async (values) => {
      await onValidate(values)
    },
  })

  const richTextRef = useRef<HURichTextInputRef | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const validateForm = async () => {
    await exerciseFormik.setFieldTouched("otherCategory")
    await exerciseFormik.setFieldTouched("description")

    const errors = await exerciseFormik.validateForm()

    if (Object.keys(errors).length === 0) {
      await exerciseFormik.submitForm()
    } else {
      setTimeout(() => {
        const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
        if (errorElements && errorElements.length > 0) {
          document
            .querySelector('[data-slot="right-sidebar-content-stack-on-top"]')
            ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 60, behavior: "smooth" })
        }
      }, 100) // Gives some time for the error tags to be added to the DOM
    }
  }

  const onCloseExerciseForm = useCallback(
    async (shouldHideSidebar: boolean) => {
      // Close the sidebar top stack and hide the sidebar if the shouldHideSidebar is true
      const closeStack = async () => {
        hideSidebarStackOnTop()
        if (shouldHideSidebar) {
          // Wait for the sidebar top stack to be hidden before hiding the sidebar
          await new Promise((resolve) => setTimeout(resolve, 50))
          hideSidebar()
        }
      }

      // If there is no updated note, close the sidebar top stack and return false
      if (!editedExercise) {
        await closeStack()
        return Promise.resolve(false)
      } else {
        // If there is an updated note and the form is dirty, show the confirm popup
        if (exerciseFormik.dirty) {
          const confirm = await new Promise<boolean>((resolve) => {
            confirmPopup.show({
              title: word("program.exercise.closeMessage.title"),
              message: word("program.exercise.closeMessage.message"),
              accept: async () => {
                await closeStack()
                return resolve(false)
              },
              cancel: () => {},
              footerProps: {
                confirmText: word("global.ok"),
              },
            })
          })
          return confirm
        } else {
          // If there is an updated note and the form is not dirty, close the sidebar top stack and return false
          await closeStack()
          return Promise.resolve(false)
        }
      }
    },
    [exerciseFormik.dirty, editedExercise],
  )

  // Defines the custom header buttons for the sidebar top stack
  const customHeaderButtons = [
    <Button
      key="close-exercise-form"
      variant="outline"
      size="iconMd"
      onClick={() => void onCloseSidebarFnRef.current()}
    >
      <XIcon color="currentColor" size={16} />
    </Button>,
  ]

  useEffect(() => {
    // Add the custom header buttons and the left button click handler to the sidebar top stack
    updateSidebarStackOnTop({
      customHeaderButtons,
      sidebarLeftButtonClick: () => onCloseExerciseFormFnRef.current(),
    })
  }, [])

  useEffect(() => {
    // Set the onCloseSidebarFnRef and onCloseExerciseFormFnRef to the onCloseExerciseForm function
    onCloseSidebarFnRef.current = () => {
      void onCloseExerciseForm(true)
    }
    onCloseExerciseFormFnRef.current = () => {
      if (currentView !== "exerciseContent") {
        setCurrentView("exerciseContent")
        return Promise.resolve(false)
      }
      return onCloseExerciseForm(false)
    }
  }, [editedExercise, exerciseFormik.dirty, currentView])

  const openChronoForm = () => {
    setCurrentView("chronoContent")
  }

  return (
    <div className={cn("flex flex-col px-5 pt-18 pb-8", className)} ref={containerRef} {...props}>
      {currentView === "exerciseContent" && (
        <>
          <div className="flex flex-col gap-8">
            <ExerciseFormHeader
              formik={exerciseFormik}
              editedExercise={editedExercise}
              updatedExerciseIndex={updatedExerciseIndex}
              richTextRef={richTextRef}
            />

            {/* Exercise settings */}
            <div className="flex flex-col gap-3">
              <div className="inline-flex items-center justify-center gap-6 self-stretch">
                <Text fontStyle="BS">{word("sessionForm.exercise.exerciseSettings")}</Text>
                <div className="h-0 flex-1 border-b border-neutral-300" />
              </div>

              <ExerciseCategoryInput formik={exerciseFormik} />
              <ExerciseDurationInput formik={exerciseFormik} />
              <ExerciseDescriptionInput richTextRef={richTextRef} formik={exerciseFormik} />
              <ExerciseRpeInput formik={exerciseFormik} />
            </div>

            {/* Score settings */}
            <div className="flex flex-col gap-3">
              <div className="inline-flex items-center justify-center gap-6 self-stretch">
                <Text fontStyle="BS">{word("sessionForm.exercise.scoreSettings")}</Text>
                <div className="h-0 flex-1 border-b border-neutral-300" />
              </div>

              <ExerciseScoreInput formik={exerciseFormik} setCurrentView={setCurrentView} />
              <ExerciseMediaRequirementInput formik={exerciseFormik} />
              <ExerciseChronoInput formik={exerciseFormik} openChronoForm={openChronoForm} />
            </div>
          </div>

          <Button variant="primary" className="mt-6 w-full" size="lg" onClick={() => void validateForm()}>
            {!!editedExercise ? word("global.save") : word("global.check")}
          </Button>
        </>
      )}

      {currentView === "scoreContent" && <ExerciseScoreForm formik={exerciseFormik} setCurrentView={setCurrentView} />}

      {currentView === "chronoContent" && (
        <ExerciseChronoForm formik={exerciseFormik} setCurrentView={setCurrentView} />
      )}
    </div>
  )
}
